// Attributes — catalog editor for the filterable car attributes
// (vehicle type, transmission, fuel, seats, …) and their values.
//
// Layout mirrors /system/templates: left sidebar lists attributes by
// English name, right pane edits the selected one. Each attribute owns
// 4 locale labels (en/fr/ar/es) and a list of value rows. Each value
// row carries its own 4 locale labels + slug + icon + position.
//
// The save flow is atomic — one PUT replaces every value-row, every
// locale row, and the parent fields in one transaction (server-side).
// That makes the UI's "save changes" affordance a true commit rather
// than an N-call sequence the operator has to monitor.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { PageHeader } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  createAttribute,
  deleteAttribute,
  deleteAttributeValueImage,
  fetchAdminAttributeValues,
  fetchAttribute,
  fetchAttributes,
  retrieveAttributeValueImage,
  saveAttribute,
  uploadAttributeValueImage,
  type AdminAttributeValueRow,
  type AttributeDisplayType,
  type AttributeListRow,
  type AttributeLocale,
  type AttributeRecord,
  type AttributeValueDraft,
  type AttributeValueRecord,
} from '@/lib/admin-api';

const LOCALES: AttributeLocale[] = ['en', 'fr', 'ar', 'es'];
const LOCALE_LABEL: Record<AttributeLocale, string> = {
  en: 'English',
  fr: 'Français',
  ar: 'العربية',
  es: 'Español',
};
const DISPLAY_TYPES: AttributeDisplayType[] = ['chips', 'checkboxes', 'range', 'toggle'];

function emptyLocale() {
  return { name: '' };
}

function emptyLocaleMap() {
  return Object.fromEntries(LOCALES.map((l) => [l, emptyLocale()])) as Record<
    AttributeLocale,
    { name: string }
  >;
}

export function Attributes() {
  const queryClient = useQueryClient();
  const listQ = useQuery({
    queryKey: ['admin', 'attributes', 'list'],
    queryFn: ({ signal }) => fetchAttributes({}, signal),
  });

  const [selected, setSelected] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const rows = listQ.data?.rows ?? [];

  // Auto-select first attribute when list loads. New attribute lands
  // selected too once create returns.
  if (selected === null && rows.length > 0 && !creating) {
    setSelected(rows[0]?.id ?? null);
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Attributes"
        sub="Catalog of filterable attributes used on cars (vehicle type, transmission, fuel, seats, features). Edits apply on the next search/filter request — no deploy needed."
        actions={
          <RowAction
            kind="primary"
            onClick={() => {
              setCreating(true);
              setSelected(null);
            }}
          >
            New attribute
          </RowAction>
        }
      />
      {listQ.error ? (
        <ErrorPanel message={(listQ.error as Error).message} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
        <aside className="rounded-xl border border-[color:var(--bo-line)] bg-white p-3">
          <SidebarList
            rows={rows}
            selected={selected}
            onSelect={(id) => {
              setSelected(id);
              setCreating(false);
            }}
          />
        </aside>

        <div className="space-y-4">
          {creating ? (
            <CreateForm
              onCancel={() => setCreating(false)}
              onCreated={async (id) => {
                setCreating(false);
                setSelected(id);
                await queryClient.invalidateQueries({
                  queryKey: ['admin', 'attributes', 'list'],
                });
              }}
            />
          ) : selected != null ? (
            <Workspace key={selected} id={selected} onDeleted={() => setSelected(null)} />
          ) : (
            <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
              {listQ.isLoading
                ? 'Loading…'
                : rows.length === 0
                ? 'No attributes yet. Click “New attribute” to seed the catalog.'
                : 'Select an attribute on the left to edit.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────

function SidebarList({
  rows,
  selected,
  onSelect,
}: {
  rows: AttributeListRow[];
  selected: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <ul className="space-y-0.5">
      {rows.map((row) => {
        const isActive = row.id === selected;
        return (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onSelect(row.id)}
              className={`flex w-full items-start justify-between gap-2 rounded-md px-2 py-1.5 text-start text-[12.5px] transition ${
                isActive
                  ? 'bg-[color:var(--bo-bg)] font-semibold text-[color:var(--bo-ink)]'
                  : 'text-[color:var(--bo-ink)]/80 hover:bg-[color:var(--bo-bg)]'
              }`}
            >
              <div className="flex flex-col">
                <span className="capitalize">{row.name ?? row.slug}</span>
                <span className="text-[11px] text-[color:var(--bo-mute)]">
                  {row.slug} · {row.displayType}
                </span>
              </div>
              <span className="rounded-full bg-[color:var(--bo-bg)] px-1.5 py-0.5 text-[10.5px] font-medium text-[color:var(--bo-mute)] tabular-nums">
                {row.valueCount}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Create new attribute ────────────────────────────────────────────

function CreateForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: number) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [displayType, setDisplayType] = useState<AttributeDisplayType>('chips');
  const [showInFilter, setShowInFilter] = useState(true);
  const [showInDetail, setShowInDetail] = useState(true);

  const createMut = useMutation({
    mutationFn: () =>
      createAttribute({
        slug: slug.trim(),
        name: name.trim(),
        displayType,
        showInFilter,
        showInDetail,
      }),
    onSuccess: (res) => onCreated(res.id),
  });

  // Auto-derive slug from name on first change (kebab-case). Operator can
  // override; once they type into slug we stop auto-syncing.
  const [slugTouched, setSlugTouched] = useState(false);

  return (
    <div className="space-y-4 rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-[15px] font-semibold tracking-tight">New attribute</h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
        >
          Cancel
        </button>
      </div>
      <Field label="Name (English)">
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          placeholder="e.g. Transmission"
          className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
      </Field>
      <Field label="Slug" hint="Lowercase letters, digits, and hyphens. Used in URLs + APIs.">
        <input
          type="text"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setSlugTouched(true);
          }}
          placeholder="e.g. transmission"
          className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 font-mono text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
      </Field>
      <Field label="Display type">
        <select
          value={displayType}
          onChange={(e) => setDisplayType(e.target.value as AttributeDisplayType)}
          className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        >
          {DISPLAY_TYPES.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </Field>
      <div className="flex flex-wrap items-center gap-4 text-[12.5px]">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showInFilter}
            onChange={(e) => setShowInFilter(e.target.checked)}
            className="h-3.5 w-3.5 accent-[color:var(--bo-accent)]"
          />
          Show in filter
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showInDetail}
            onChange={(e) => setShowInDetail(e.target.checked)}
            className="h-3.5 w-3.5 accent-[color:var(--bo-accent)]"
          />
          Show in car detail
        </label>
      </div>
      {createMut.error ? (
        <ErrorPanel message={(createMut.error as Error).message} />
      ) : null}
      <div className="flex justify-end">
        <RowAction
          kind="primary"
          disabled={!name.trim() || !slug.trim() || createMut.isPending}
          busy={createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          Create attribute
        </RowAction>
      </div>
    </div>
  );
}

// ─── Per-attribute editor ────────────────────────────────────────────

function Workspace({ id, onDeleted }: { id: number; onDeleted: () => void }) {
  const queryClient = useQueryClient();
  const recordQ = useQuery<AttributeRecord, Error>({
    queryKey: ['admin', 'attributes', 'record', id],
    queryFn: ({ signal }) => fetchAttribute(id, signal),
  });

  const [draft, setDraft] = useState<AttributeRecord | null>(null);
  const [locale, setLocale] = useState<AttributeLocale>('en');
  useEffect(() => {
    if (recordQ.data) setDraft(recordQ.data);
  }, [recordQ.data]);

  const dirty = useMemo(() => {
    if (!draft || !recordQ.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(recordQ.data);
  }, [draft, recordQ.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('Nothing to save');
      return saveAttribute(id, {
        slug: draft.slug,
        displayType: draft.displayType,
        showInFilter: draft.showInFilter ?? undefined,
        showInDetail: draft.showInDetail ?? undefined,
        position: draft.position ?? undefined,
        locales: draft.locales,
        values: draft.values.map<AttributeValueDraft>((v) => ({
          id: v.id < 0 ? undefined : v.id,
          slug: v.slug,
          position: v.position,
          icon: v.icon,
          imageId: v.imageId,
          // Only meaningful for Avis car-groups — omit elsewhere so the
          // server leaves mapped_vehicle_types untouched.
          dominantVehicleType:
            v.avisExamples != null || v.mappedVehicleTypes != null
              ? v.mappedVehicleTypes?.dominant ?? null
              : undefined,
          locales: v.locales,
        })),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'attributes', 'record', id],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'attributes', 'list'],
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteAttribute(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'attributes', 'list'] });
      onDeleted();
    },
  });

  // Vehicle-type options for the Avis car-group crosswalk select. Only
  // fetched when the edited attribute actually has Avis-enriched values.
  const hasAvisValues =
    draft?.values.some((v) => v.avisExamples != null || v.mappedVehicleTypes != null) ?? false;
  const vehicleTypesQ = useQuery({
    queryKey: ['admin', 'attribute-values', 'picker', 'vehicle-type-options'],
    queryFn: ({ signal }) => fetchAdminAttributeValues({ limit: 200 }, signal),
    enabled: hasAvisValues,
    staleTime: 300_000,
  });
  const vehicleTypeOptions = (vehicleTypesQ.data?.rows ?? []).filter(
    (r) => r.attributeSlug === 'vehicle-type',
  );

  if (recordQ.isLoading || !draft) {
    return (
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
        Loading attribute…
      </div>
    );
  }
  if (recordQ.error) {
    return <ErrorPanel message={(recordQ.error as Error).message} />;
  }

  const setLocaleField = (loc: AttributeLocale, field: 'name', value: string) => {
    setDraft((d) =>
      d
        ? {
            ...d,
            locales: { ...d.locales, [loc]: { ...d.locales[loc], [field]: value } },
          }
        : d,
    );
  };

  const setValueLocaleField = (
    valueIdx: number,
    loc: AttributeLocale,
    value: string,
  ) => {
    setDraft((d) => {
      if (!d) return d;
      const next = [...d.values];
      const target = next[valueIdx];
      if (!target) return d;
      next[valueIdx] = {
        ...target,
        locales: { ...target.locales, [loc]: { name: value } },
      };
      return { ...d, values: next };
    });
  };

  const updateValueField = (idx: number, patch: Partial<AttributeRecord['values'][number]>) => {
    setDraft((d) => {
      if (!d) return d;
      const next = [...d.values];
      const target = next[idx];
      if (!target) return d;
      next[idx] = { ...target, ...patch };
      return { ...d, values: next };
    });
  };

  const addValue = () => {
    setDraft((d) =>
      d
        ? {
            ...d,
            values: [
              ...d.values,
              {
                id: -(d.values.length + 1), // negative sentinel — server treats as new
                slug: '',
                position: d.values.length,
                icon: null,
                imageId: null,
                imageUrl: null,
                avisExamples: null,
                mappedVehicleTypes: null,
                locales: emptyLocaleMap(),
              },
            ],
          }
        : d,
    );
  };

  const removeValue = (idx: number) => {
    setDraft((d) => {
      if (!d) return d;
      const next = [...d.values];
      next.splice(idx, 1);
      return { ...d, values: next };
    });
  };

  // Image upload/remove happens against the server immediately (not part of
  // the atomic save), so patch BOTH the draft and the cached record: the
  // draft so a later full-replacement save round-trips the new imageId, the
  // cache so the change doesn't read as a phantom "unsaved" diff.
  const syncValueImage = (valueId: number, imageId: number | null, imageUrl: string | null) => {
    const patch = (r: AttributeRecord): AttributeRecord => ({
      ...r,
      values: r.values.map((v) => (v.id === valueId ? { ...v, imageId, imageUrl } : v)),
    });
    setDraft((d) => (d ? patch(d) : d));
    queryClient.setQueryData<AttributeRecord>(
      ['admin', 'attributes', 'record', id],
      (old) => (old ? patch(old) : old),
    );
  };

  const moveValue = (idx: number, delta: -1 | 1) => {
    setDraft((d) => {
      if (!d) return d;
      const next = [...d.values];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return d;
      const a = next[idx];
      const b = next[j];
      if (!a || !b) return d;
      next[idx] = b;
      next[j] = a;
      return {
        ...d,
        values: next.map((v, i) => ({ ...v, position: i })),
      };
    });
  };

  return (
    <>
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
              Attribute
            </div>
            <div className="text-[16px] font-semibold capitalize tracking-tight">
              {draft.locales.en?.name || draft.slug}
            </div>
            <div className="mt-0.5 text-[11.5px] text-[color:var(--bo-mute)]">
              {draft.slug} · {draft.values.length} value{draft.values.length === 1 ? '' : 's'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              const ok = window.confirm(
                `Delete attribute "${draft.locales.en?.name || draft.slug}"? This cannot be undone.`,
              );
              if (ok) deleteMut.mutate();
            }}
            disabled={deleteMut.isPending}
            className="inline-flex h-8 items-center rounded-lg border border-[#fecaca] px-3 text-[12px] font-medium text-[#9f1239] transition hover:bg-[#fff1f2] disabled:opacity-50"
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        {deleteMut.error ? (
          <div className="mt-2 text-[12px] text-[#9f1239]">
            {(deleteMut.error as Error).message.includes('in_use')
              ? 'Cannot delete — at least one car still uses this attribute. Re-tag those cars first.'
              : `Delete failed: ${(deleteMut.error as Error).message}`}
          </div>
        ) : null}
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Slug">
            <input
              type="text"
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 font-mono text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </Field>
          <Field label="Display type">
            <select
              value={draft.displayType}
              onChange={(e) =>
                setDraft({ ...draft, displayType: e.target.value as AttributeDisplayType })
              }
              className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            >
              {DISPLAY_TYPES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Position" hint="Lower number sorts first.">
            <input
              type="number"
              value={draft.position ?? 0}
              onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) })}
              className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-[12.5px]">
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={Boolean(draft.showInFilter)}
              onChange={(e) => setDraft({ ...draft, showInFilter: e.target.checked })}
              className="h-3.5 w-3.5 accent-[color:var(--bo-accent)]"
            />
            Show in filter
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={Boolean(draft.showInDetail)}
              onChange={(e) => setDraft({ ...draft, showInDetail: e.target.checked })}
              className="h-3.5 w-3.5 accent-[color:var(--bo-accent)]"
            />
            Show in car detail
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1">
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLocale(l)}
              className={`rounded-md border px-2 py-1 text-[12px] font-medium ${
                locale === l
                  ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
                  : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
              }`}
            >
              {LOCALE_LABEL[l]}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <Field label={`Name (${LOCALE_LABEL[locale]})`}>
            <input
              type="text"
              dir={locale === 'ar' ? 'rtl' : 'ltr'}
              value={draft.locales[locale]?.name ?? ''}
              onChange={(e) => setLocaleField(locale, 'name', e.target.value)}
              className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </Field>
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight">Values</h3>
          <RowAction kind="ghost" onClick={addValue}>
            Add value
          </RowAction>
        </div>
        {draft.values.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[color:var(--bo-line)] p-4 text-[12px] text-[color:var(--bo-mute)]">
            No values yet. Add at least one to make this attribute selectable.
          </div>
        ) : (
          <ul className="space-y-2">
            {draft.values.map((v, idx) => (
              <li
                key={v.id}
                className="grid grid-cols-1 gap-2 rounded-lg border border-[color:var(--bo-line)] bg-white p-3 md:grid-cols-[auto_1fr_1fr_120px_auto]"
              >
                <ValueImageCell
                  attributeId={id}
                  value={v}
                  onChanged={(imageId, imageUrl) => syncValueImage(v.id, imageId, imageUrl)}
                />
                <div>
                  <label className="mb-1 block text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={v.slug}
                    onChange={(e) => updateValueField(idx, { slug: e.target.value })}
                    className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 font-mono text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                    Name ({LOCALE_LABEL[locale]})
                  </label>
                  <input
                    type="text"
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                    value={v.locales[locale]?.name ?? ''}
                    onChange={(e) => setValueLocaleField(idx, locale, e.target.value)}
                    className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                    Icon
                  </label>
                  <input
                    type="text"
                    value={v.icon ?? ''}
                    onChange={(e) => updateValueField(idx, { icon: e.target.value || null })}
                    placeholder="emoji or class"
                    className="h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                  />
                </div>
                <div className="flex items-end justify-end gap-1">
                  <IconBtn title="Move up" onClick={() => moveValue(idx, -1)} disabled={idx === 0}>
                    ↑
                  </IconBtn>
                  <IconBtn
                    title="Move down"
                    onClick={() => moveValue(idx, 1)}
                    disabled={idx === draft.values.length - 1}
                  >
                    ↓
                  </IconBtn>
                  <IconBtn title="Remove" onClick={() => removeValue(idx)} variant="danger">
                    ×
                  </IconBtn>
                </div>
                {/* CAPACITY, for transfer classes.
                    These are not decoration: checkout refuses a party of four
                    in a 3-seat Economy using exactly these numbers, and until
                    now they were settable only by SQL — so a class that could
                    not honour its advertised capacity waited for a developer.
                    Shown only where the concept applies; a fuel type has no
                    seats. */}
                {draft.slug === 'transfer-class' && (
                  <div className="md:col-span-full">
                    <div className="flex flex-wrap items-center gap-3 rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] p-2.5">
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
                        Capacity
                      </span>
                      <label className="flex items-center gap-1.5 text-[12px]">
                        Seats
                        <input
                          type="number"
                          min={1}
                          max={60}
                          value={v.maxPassengers ?? ''}
                          onChange={(e) =>
                            updateValueField(idx, {
                              // Empty CLEARS it rather than storing 0 — a class
                              // with no stated capacity is a real state, and 0
                              // seats would refuse every booking.
                              maxPassengers: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          className="h-7 w-16 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                        />
                      </label>
                      <label className="flex items-center gap-1.5 text-[12px]">
                        Bags
                        <input
                          type="number"
                          min={0}
                          max={60}
                          value={v.maxLuggage ?? ''}
                          onChange={(e) =>
                            updateValueField(idx, {
                              maxLuggage: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          className="h-7 w-16 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
                        />
                      </label>
                      <span className="text-[11px] text-[color:var(--bo-mute)]">
                        Seats gate checkout — a larger party cannot book this class.
                      </span>
                    </div>
                  </div>
                )}
                {(v.avisExamples != null || v.mappedVehicleTypes != null) && (
                  <div className="md:col-span-full">
                    <AvisGroupPanel
                      value={v}
                      vehicleTypes={vehicleTypeOptions}
                      onDominantChange={(slug) =>
                        updateValueField(idx, {
                          mappedVehicleTypes: { ...v.mappedVehicleTypes, dominant: slug },
                        })
                      }
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[11px] text-[color:var(--bo-mute)]">
          Tip: use the locale tabs above to edit per-language names for both the attribute and its values.
        </p>
      </div>

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(recordQ.data ?? null)}
        onSave={() => saveMut.mutate()}
      />
    </>
  );
}

// ─── Avis car-group panel ────────────────────────────────────────────
//
// Shown under an Avis-enriched value row: the per-market example cars the
// scraper collected (read-only — the feed refresh rewrites them) and the
// dominant vehicle-type crosswalk (editable; per-market overrides from
// migration 0127 are preserved server-side and summarized here).

function AvisGroupPanel({
  value,
  vehicleTypes,
  onDominantChange,
}: {
  value: AttributeValueRecord;
  vehicleTypes: AdminAttributeValueRow[];
  onDominantChange: (slug: string | null) => void;
}) {
  const examples = Object.entries(value.avisExamples ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const byCountry = value.mappedVehicleTypes?.byCountry ?? {};
  const overrideCount = Object.keys(byCountry).length;
  const dominant = value.mappedVehicleTypes?.dominant ?? '';

  return (
    <div className="rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] p-2.5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Avis group
        </span>
        <label className="flex items-center gap-1.5 text-[12px]">
          Vehicle type
          <select
            value={dominant}
            onChange={(e) => onDominantChange(e.target.value || null)}
            className="h-7 rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
          >
            <option value="">Not linked</option>
            {vehicleTypes.map((t) => (
              <option key={t.id} value={t.slug}>
                {t.name ?? t.slug}
              </option>
            ))}
            {dominant !== '' && !vehicleTypes.some((t) => t.slug === dominant) && (
              <option value={dominant}>{dominant}</option>
            )}
          </select>
        </label>
        {overrideCount > 0 && (
          <span
            className="text-[11px] text-[color:var(--bo-mute)]"
            title={Object.entries(byCountry)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([iso, t]) => `${iso.toUpperCase()}: ${t}`)
              .join('\n')}
          >
            {overrideCount} per-market override{overrideCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {examples.length > 0 && (
        <details className="mt-1.5">
          <summary className="cursor-pointer text-[11.5px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]">
            Example cars — {examples.length} market{examples.length === 1 ? '' : 's'}
            {examples[0] ? ` (e.g. ${examples[0][0].toUpperCase()}: ${examples[0][1]})` : ''}
          </summary>
          <ul className="mt-1 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3">
            {examples.map(([iso, car]) => (
              <li key={iso} className="text-[11.5px] text-[color:var(--bo-ink)]">
                <span className="font-mono uppercase text-[color:var(--bo-mute)]">{iso}</span> {car}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ─── Value image cell ────────────────────────────────────────────────
//
// Thumbnail + upload/replace/remove/AI-generate for one attribute value's
// category image (e.g. the vehicle-type SUV / Sedan art the storefront uses
// as the last-resort listing image). Uploads hit the server immediately
// (presign → S3 PUT → finalize); AI generation is async server-side (202)
// and we poll the attribute record until the image lands. The parent syncs
// draft + query cache. Unsaved value rows (negative sentinel id) can't hold
// an image yet.

function ValueImageCell({
  attributeId,
  value,
  onChanged,
}: {
  attributeId: number;
  value: AttributeValueRecord;
  onChanged: (imageId: number | null, imageUrl: string | null) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const persisted = value.id > 0;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await uploadAttributeValueImage(value.id, file);
      onChanged(res.mediaId, res.url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteAttributeValueImage(value.id);
      onChanged(null, null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const baselineImageId = value.imageId;
      await retrieveAttributeValueImage(value.id);
      // Poll for up to 3 minutes — generation runs server-side in the
      // background (40-90s typical).
      for (let attempt = 0; attempt < 36; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const fresh = await fetchAttribute(attributeId);
        const freshValue = fresh.values.find((v) => v.id === value.id);
        if (freshValue && freshValue.imageId != null && freshValue.imageId !== baselineImageId) {
          onChanged(freshValue.imageId, freshValue.imageUrl);
          return;
        }
      }
      setError('Still generating — reload in a minute.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="w-[104px]">
      <label className="mb-1 block text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
        Image
      </label>
      <div className="flex items-center gap-1.5">
        {value.imageUrl ? (
          <img
            src={value.imageUrl}
            alt={value.slug}
            className="h-8 w-8 rounded-md border border-[color:var(--bo-line)] object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-md border border-dashed border-[color:var(--bo-line)] text-[10px] text-[color:var(--bo-mute)]">
            —
          </div>
        )}
        {persisted ? (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              disabled={busy || generating}
              onClick={() => fileRef.current?.click()}
              className="text-start text-[11px] font-medium text-[color:var(--bo-accent)] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Working…' : value.imageUrl ? 'Replace' : 'Upload'}
            </button>
            <button
              type="button"
              disabled={busy || generating}
              onClick={generate}
              title="Generate category art with AI. Takes 1-2 minutes; replaces the current image."
              className="text-start text-[11px] font-medium text-[color:var(--bo-accent)] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generating ? 'Generating…' : 'Generate (AI)'}
            </button>
            {value.imageUrl != null && !busy && !generating && (
              <button
                type="button"
                onClick={remove}
                className="text-start text-[11px] text-[#9f1239] hover:underline"
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <span
            className="text-[10.5px] leading-tight text-[color:var(--bo-mute)]"
            title="Save the attribute first, then upload an image for this value."
          >
            Save first
          </span>
        )}
      </div>
      {error != null && (
        <div className="mt-1 max-w-[104px] text-[10.5px] leading-tight text-[#9f1239]" title={error}>
          {error.includes('ai_not_configured')
            ? 'AI is not configured yet.'
            : error.includes('Still generating')
              ? error
              : 'Failed — try again.'}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        aria-label={`Upload image for ${value.slug || 'value'}`}
      />
    </div>
  );
}

// ─── Primitives ──────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-[color:var(--bo-mute)]">{hint}</p> : null}
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  title,
  variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  variant?: 'danger';
}) {
  const danger = variant === 'danger';
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md border text-[14px] transition ${
        danger
          ? 'border-[#fecaca] text-[#9f1239] hover:bg-[#fff1f2]'
          : 'border-[color:var(--bo-line)] text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky bottom-2 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
    >
      {message}
    </div>
  );
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}
